(() => {
  const STORAGE_KEY_PREFIX = "ymm_garage_";
  const LEGACY_SELECTION_KEY = "ymm_equipment";

  // Marks that this browsing session has already taken its copy of the garage from the
  // account. sessionStorage, not localStorage: a new visit must go back to the account,
  // which is what makes a change made on another device show up here.
  const SEEDED_FLAG_PREFIX = "ymm_garage_seeded_";

  // Where "shop my equipment" sends the shopper. It is the ordinary catalogue collection:
  // compatibility is NOT a Shopify facet - the YMM app owns the part<->equipment mappings
  // and Shopify holds none of them - so this collection cannot actually narrow to the
  // unit yet. The equipment still travels in the query string so whatever reads it later
  // has it; until then the shopper sees the full catalogue.
  const SHOP_PATH = "/collections/all";

  // Storage seam. localStorage is always the read path because every caller below is
  // synchronous; a shopper's own change is also mirrored to the customer metafield
  // (KNEK-285) so the garage follows the customer to another device. Liquid renders that
  // metafield back into the page and localStorage is seeded from it on first load - see
  // `_maybeSeedFromMetafield`.
  const localStorageAdapter = {
    read(key) {
      try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        console.warn("GarageService: failed to read garage", e);
        return null;
      }
    },

    // Only a shopper's own change belongs on the account. `mirror: false` is for the
    // writes that merely rearrange what this device already had - seeding, the guest
    // hand-over at login, the legacy-key carry-over - all of which run inside `_load()`
    // on an ordinary page view. Mirroring those would push a device's local garage into
    // the account with no shopper action, and would resurrect a metafield deleted in the
    // admin on the very next page load.
    write(key, value, { mirror = true } = {}) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {
        console.warn("GarageService: failed to write garage", e);
      }
      if (mirror) mirrorToMetafield(value);
    },
  };

  // Coalescing is done here rather than with the shared `debounce` helper because the
  // pending write has to be reachable: the shopper can save a unit and click a link
  // inside the delay, and the next page reads the account, so a lost write would show
  // them a garage without the unit they just saved.
  const MIRROR_DELAY = 500;
  let mirrorTimer = null;
  let pendingMirror = null;

  const mirrorBody = (value) =>
    JSON.stringify({
      metafields: [
        {
          namespace: "garage",
          key: "equipment",
          type: "json",
          value: JSON.stringify(value),
        },
      ],
    });

  // Fire-and-forget: a failed mirror must never block the UI, because localStorage
  // already holds the copy this page is rendering from.
  function flushMirror() {
    window.clearTimeout(mirrorTimer);
    mirrorTimer = null;
    if (!pendingMirror) return;

    const { endpoint, value } = pendingMirror;
    pendingMirror = null;

    fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: mirrorBody(value),
      // The request must survive the shopper navigating away mid-flight.
      keepalive: true,
    }).catch((e) => console.warn("GarageService: failed to mirror garage to metafield", e));
  }

  // `fetch` is cancelled once the page is gone, so a write still inside the delay leaves
  // on a beacon instead - the browser sends it after the document is discarded.
  window.addEventListener("pagehide", () => {
    if (!pendingMirror) return;

    const { endpoint, value } = pendingMirror;
    pendingMirror = null;
    window.clearTimeout(mirrorTimer);
    mirrorTimer = null;

    const body = new Blob([mirrorBody(value)], { type: "application/json" });
    if (!navigator.sendBeacon?.(endpoint, body)) {
      console.warn("GarageService: failed to flush garage on pagehide");
    }
  });

  // The guard stays out here so a guest, or a store with no endpoint configured, never
  // schedules a timer at all.
  function mirrorToMetafield(value) {
    const endpoint = window.theme?.garage?.endpoint;
    if (!endpoint || !window.theme?.customerId) return;

    pendingMirror = { endpoint, value };
    window.clearTimeout(mirrorTimer);
    mirrorTimer = window.setTimeout(flushMirror, MIRROR_DELAY);
  }

  // Private-mode browsers throw on sessionStorage rather than returning null. Failing to
  // read is treated as "not seeded yet": the shopper still gets the account copy, they
  // just get it again on the next page instead of once per session.
  const sessionFlag = {
    read(key) {
      try {
        return sessionStorage.getItem(key) !== null;
      } catch (e) {
        return false;
      }
    },

    write(key) {
      try {
        sessionStorage.setItem(key, "1");
      } catch (e) {
        /* nothing to do - see above */
      }
    },
  };

  const emptyGarage = () => ({ items: [], activeId: null, selection: null });

  class GarageService {
    constructor() {
      this.adapter = localStorageAdapter;
      this._customerId = null;
      this._seededFromMetafield = false;
      this._adoptedLegacySelection = false;

      // Dispatch ready event immediately after instantiation
      // Since this script is deferred, it runs after parsing but before DOMContentLoaded
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("garage:ready"));
      }, 0);
    }

    get customerId() {
      // Lazy access to window.theme.customerId
      // This ensures we get it even if window.theme is defined later in the body
      const cid = window.theme?.customerId || null;

      // Update internal cache if it changed (e.g. from null to value)
      if (cid && cid !== this._customerId) {
        this._customerId = cid;
      }
      return this._customerId;
    }

    get storageKey() {
      const cid = this.customerId;
      // If customer ID is available, use it. Otherwise use 'guest'.
      return cid ? `${STORAGE_KEY_PREFIX}${cid}` : `${STORAGE_KEY_PREFIX}guest`;
    }

    // A signed-in shopper's garage lives on the account, so the copy Liquid renders into
    // `window.theme.garage.saved` replaces whatever this device held - including with
    // nothing, when the account has no garage. That is what lets a unit saved on another
    // device turn up here.
    //
    // Once per session, not once per page. Every change also posts back to the account,
    // but that request is in flight while the next page is already being rendered: seeding
    // again on that page would hand the shopper the pre-save account copy and lose the
    // unit they just saved. Within a session localStorage is the fresher of the two.
    _maybeSeedFromMetafield() {
      const cid = this.customerId;
      if (!cid || this._seededFromMetafield) return;
      this._seededFromMetafield = true;

      // No `garage` object means the layout did not render the account copy at all, which
      // is not the same as an empty account - overwriting on that would wipe the garage.
      if (!window.theme?.garage) return;

      const seededKey = `${SEEDED_FLAG_PREFIX}${cid}`;
      if (sessionFlag.read(seededKey)) return;

      const customerKey = `${STORAGE_KEY_PREFIX}${cid}`;
      this.adapter.write(customerKey, this._normalize(window.theme.garage.saved), { mirror: false });
      sessionFlag.write(seededKey);
    }

    // The selector used to keep the pending selection under its own `ymm_equipment` key.
    // Carry it across once so the change does not visibly drop a shopper's selection.
    _maybeAdoptLegacySelection() {
      if (this._adoptedLegacySelection) return;
      this._adoptedLegacySelection = true;

      let legacy = null;
      try {
        const raw = localStorage.getItem(LEGACY_SELECTION_KEY);
        legacy = raw ? JSON.parse(raw) : null;
        localStorage.removeItem(LEGACY_SELECTION_KEY);
      } catch (e) {
        return;
      }

      const selection = this.normalizeEquipment(legacy);
      if (!selection) return;

      const garage = this._normalize(this.adapter.read(this.storageKey));
      if (garage.selection) return;

      this.adapter.write(this.storageKey, { ...garage, selection }, { mirror: false });
    }

    // Legacy payloads were a bare array of items keyed by `brand` and carrying `year`.
    // KNEK-246 dropped Year from the YMM identity and renamed the dimension to `make`,
    // so normalize on read and keep an existing garage working across the change.
    _normalize(payload) {
      if (!payload) return emptyGarage();

      // The metafield reaches us as an object when its definition is `json` and as a
      // JSON string when it is a text type. Accept both so the theme keeps working
      // whichever way the definition is created.
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch (e) {
          return emptyGarage();
        }
        if (!payload) return emptyGarage();
      }

      // The oldest shape was a bare array of units, before the garage grew an active unit
      // and a pending selection.
      if (Array.isArray(payload)) payload = { items: payload };
      if (!Array.isArray(payload.items)) return emptyGarage();

      const items = payload.items.map((item) => this.normalizeEquipment(item)).filter(Boolean);
      const storedActiveId = payload.activeId;
      const activeId = items.some((item) => item.id === storedActiveId) ? storedActiveId : items[0]?.id || null;
      const selection = this.normalizeEquipment(payload.selection);

      return { items, activeId, selection };
    }

    normalizeEquipment(equipment) {
      if (!equipment) return null;

      const make = equipment.make || equipment.brand;
      const model = equipment.model;
      if (!make?.id || !model?.id) return null;

      const normalized = {
        make: { id: make.id, name: make.name },
        model: { id: model.id, name: model.name },
        serial: equipment.serial || "",
        name: [make.name, model.name].filter(Boolean).join(" "),
        addedAt: equipment.addedAt || Date.now(),
      };
      normalized.id = this._generateId(normalized);

      return normalized;
    }

    _load() {
      // Order matters: the account copy lands first, so the legacy selection is only
      // adopted once the key it would be written to is settled.
      this._maybeSeedFromMetafield();
      this._maybeAdoptLegacySelection();

      return this._normalize(this.adapter.read(this.storageKey));
    }

    getGarage() {
      return this._load();
    }

    // The unit the shopper is currently on: a pending selection if there is one, otherwise
    // the active garage unit. A selection is what the shopper picked in the selector but
    // has not saved: it rides in the garage payload rather than a second storage key so
    // there is one source of truth, but it stays out of `items` because saving is an
    // explicit act.
    //
    // `snippets/ymm-selector.liquid` re-derives the same precedence in Liquid so the
    // selector paints its saved unit before this file runs - change one and change both.
    getCurrent() {
      const { items, activeId, selection } = this._load();
      return selection || items.find((item) => item.id === activeId) || null;
    }

    setSelection(equipment) {
      const selection = this.normalizeEquipment(equipment);
      if (!selection) return;

      const garage = this._load();
      this._commit({ ...garage, selection }, garage.activeId);
    }

    clearSelection() {
      const garage = this._load();
      if (!garage.selection) return;

      this._commit({ ...garage, selection: null }, garage.activeId);
    }

    add(equipment) {
      const item = this.normalizeEquipment(equipment);
      if (!item) return;

      const garage = this._load();
      const isSaved = garage.items.some((saved) => saved.id === item.id);

      // Saving a unit that is already in the garage promotes it to active
      // instead of adding a duplicate row.
      const items = isSaved ? garage.items : [item, ...garage.items];

      // The unit is in the garage now, so it is no longer a pending selection. Callers
      // that need to display it read `getCurrent()`.
      this._commit({ items, activeId: item.id, selection: null }, garage.activeId);
    }

    setActive(id) {
      const garage = this._load();
      if (garage.activeId === id) return;
      if (!garage.items.some((item) => item.id === id)) return;

      this._commit({ ...garage, activeId: id }, garage.activeId);
    }

    remove(id) {
      const garage = this._load();
      const items = garage.items.filter((item) => item.id !== id);
      const activeId = garage.activeId === id ? items[0]?.id || null : garage.activeId;

      this._commit({ ...garage, items, activeId }, garage.activeId);
    }

    // The single place that turns an equipment record into a storefront URL, so the day
    // compatibility becomes filterable only this method changes.
    buildShopUrl(equipment) {
      const params = this.buildEquipmentParams(equipment);

      return params ? `${SHOP_PATH}?${params}` : SHOP_PATH;
    }

    // The query-string contract for an equipment link, kept here so the side that writes
    // the link and any future side that reads it cannot drift apart.
    //
    // The values are the YMM keyword strings, not the ids: they are meant to go straight
    // into an Elasticsearch `term` filter, and the index stores the display string. Units
    // captured from a product page carry a slugged id (`john-deere`) that would never
    // match, so the name is what travels.
    buildEquipmentParams(equipment) {
      const item = this.normalizeEquipment(equipment);
      if (!item) return null;

      const params = new URLSearchParams();
      params.set("make", item.make.name || item.make.id);
      params.set("model", item.model.name || item.model.id);
      if (item.serial) params.set("serial", item.serial);

      return params;
    }

    _commit(garage, previousActiveId) {
      this.adapter.write(this.storageKey, garage);

      window.dispatchEvent(new CustomEvent("garage:updated", { detail: garage }));

      if (garage.activeId !== previousActiveId) {
        const active = garage.items.find((item) => item.id === garage.activeId) || null;
        window.dispatchEvent(new CustomEvent("garage:active-changed", { detail: { equipment: active } }));
      }
    }

    // Serial is not part of the YMM identity yet (KNEK-246 keeps `model_number` empty
    // until KNEK-244 defines the buckets), but it still distinguishes two units of the
    // same model inside a customer's own garage.
    _generateId(equipment) {
      return `${equipment.make.id}:${equipment.model.id}:${equipment.serial}`.toLowerCase().replace(/\s+/g, "-");
    }
  }

  // Initialize and expose
  window.garageService = new GarageService();
})();
